import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { FolderIcon, ChevronRight, Home, Loader2 } from "lucide-react";
import { useFolders, type FolderTreeNode } from "@/hooks/useFolders";
import { cn } from "@/lib/utils";

interface MoveToFolderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onMove: (folderId: string | null) => Promise<void>;
  currentFolderId?: string | null;
  documentTitle?: string;
}

const FolderItem = ({
  folder,
  level = 0,
  selectedId,
  onSelect,
  currentFolderId,
}: {
  folder: FolderTreeNode;
  level?: number;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  currentFolderId?: string | null;
}) => {
  const [expanded, setExpanded] = useState(true);
  const hasChildren = folder.children.length > 0;
  const isSelected = selectedId === folder.id;
  const isCurrent = folder.id === currentFolderId;

  return (
    <div>
      <button
        type="button"
        onClick={() => onSelect(folder.id)}
        disabled={isCurrent}
        className={cn(
          "w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors text-left",
          isSelected
            ? "bg-primary text-primary-foreground"
            : "hover:bg-muted",
          isCurrent && "opacity-50 cursor-not-allowed"
        )}
        style={{ paddingLeft: `${level * 16 + 8}px` }}
      >
        {hasChildren && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(!expanded);
            }}
            className="p-0.5 hover:bg-background/50 rounded"
          >
            <ChevronRight
              className={cn(
                "w-3 h-3 transition-transform",
                expanded && "rotate-90"
              )}
            />
          </button>
        )}
        {!hasChildren && <span className="w-4" />}
        <FolderIcon
          className="w-4 h-4 shrink-0"
          style={{ color: folder.color || "#6366f1" }}
        />
        <span className="truncate flex-1">{folder.name}</span>
        {isCurrent && (
          <span className="text-[10px] text-muted-foreground">(actuel)</span>
        )}
      </button>

      {hasChildren && expanded && (
        <div>
          {folder.children.map((child) => (
            <FolderItem
              key={child.id}
              folder={child}
              level={level + 1}
              selectedId={selectedId}
              onSelect={onSelect}
              currentFolderId={currentFolderId}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export function MoveToFolderDialog({
  open,
  onOpenChange,
  onMove,
  currentFolderId,
  documentTitle,
}: MoveToFolderDialogProps) {
  const { folderTree, loading } = useFolders();
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [moving, setMoving] = useState(false);

  const handleMove = async () => {
    setMoving(true);
    try {
      await onMove(selectedFolderId);
      onOpenChange(false);
    } finally {
      setMoving(false);
    }
  };

  const isRootSelected = selectedFolderId === null && currentFolderId !== null;
  const canMove = selectedFolderId !== currentFolderId;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Déplacer vers un dossier</DialogTitle>
          {documentTitle && (
            <DialogDescription className="truncate">
              {documentTitle}
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="py-2">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <ScrollArea className="h-[300px] pr-3">
              {/* Root option */}
              <button
                type="button"
                onClick={() => setSelectedFolderId(null)}
                disabled={currentFolderId === null}
                className={cn(
                  "w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors text-left mb-1",
                  selectedFolderId === null
                    ? "bg-primary text-primary-foreground"
                    : "hover:bg-muted",
                  currentFolderId === null && "opacity-50 cursor-not-allowed"
                )}
              >
                <Home className="w-4 h-4 shrink-0" />
                <span>Aucun dossier (racine)</span>
                {currentFolderId === null && (
                  <span className="text-[10px] text-muted-foreground ml-auto">
                    (actuel)
                  </span>
                )}
              </button>

              {/* Folder tree */}
              {folderTree.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  Aucun dossier créé
                </p>
              ) : (
                folderTree.map((folder) => (
                  <FolderItem
                    key={folder.id}
                    folder={folder}
                    selectedId={selectedFolderId}
                    onSelect={setSelectedFolderId}
                    currentFolderId={currentFolderId}
                  />
                ))
              )}
            </ScrollArea>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button onClick={handleMove} disabled={!canMove || moving}>
            {moving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Déplacer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
